import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useReactFlow, useNodes, useEdges } from "reactflow";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { RootState } from "../../redux/store";
import { addUpdateNodeToQueue, handleWorkflowRun, handleWorkflowSave, formatConnectedOutputValue } from "../../services/method";
import { LoopProject } from "./loop";
import { ExportProject } from "./export";
import { ChatProject } from "./chat";
import { APIProject } from "./API";
import { WebChatProject } from "./webChat";
import { GlobalConnectionsPanel } from "./globalConnections";
// import { reduxUpdateError } from "../../redux/reducers/error";
import { Main } from "./style";
// import { cancelAllRequests, webURL } from "../../services/API";
// import axios from "axios";
// import { reduxUpdateSettings } from "../../redux/reducers/settings";
import { operationType } from "../../services/types";
// import { reduxUpdatePrevUser } from "../../redux/reducers/user";
import { TextInput } from "../form/textInput";
import { X, AlertTriangle, Zap, ListChecks } from "lucide-react";
import { applyAutoLayout } from "../../services/autoLayout";
import socket from "../../services/socket";
import { reduxUpdateSettings } from "../../redux/reducers/settings";
import { consoleURL } from "../../services/API";
import {
   collectWorkflowIssues,
   toggleWorkflowIssuesPanel,
   WORKFLOW_CONNECTIONS_OPEN_EVENT,
} from "../../services/validateWorkflowIssues";
import {
   advanceExecutionProgress,
   clearExecutionProgress,
   isExecutionErrorOutput,
   markRunEntryNodesExecuting,
} from "../../services/executionProgress";
import { createPortal } from "react-dom";
import { PopupOverlay } from "./popupOverlay";
import { TriggerDropdown } from "./triggerSelect/style";

let intervalId: any = 0;
const TRIGGER_ACTIONS = new Set([
   'start', 'webhook',
   'whatsappListener', 'whatsappCloudTrigger', 'metaListenerTrigger',
   'shopifyTrigger', 'odooTrigger', 'easyOrdersTrigger', 'bostaTrigger', 'woocommerceTrigger',
   'sqlTrigger', 'googleDriveTrigger', 'googleCalendarTrigger', 'slackTrigger', 'teamsTrigger', 'sharepointTrigger', 
   'typeformTrigger', 'googleFormTrigger', 'outlookTrigger', 'imapTrigger',
   'oneDriveTrigger',
]);

export function RunActions({ project, setProject, onUndo, onRedo, canUndo, canRedo }: {
   project:    projectType | null;
   setProject: React.Dispatch<React.SetStateAction<projectType | null>>;
   onUndo:     () => void;
   onRedo:     () => void;
   canUndo:    boolean;
   canRedo:    boolean;
}) {
   const navigate = useNavigate();
   const dispatch = useDispatch();
   const userToken = useSelector((state: RootState) => state.user.data?.token);
   const isWorkflowRun = useSelector((state: RootState) => state.settings.data.isWorkflowRun);
   const [state, setState] = React.useState({ isRun: false, isLoop: false, isExport: false, isChat: false, isAPI: false, isWebChat: false, isReset: false, isRunRealTime: false, isGlobalConnections: false });
   const [triggerNodes, setTriggerNodes] = React.useState<Array<{ index: string, name: string }> | null>(null);
   const runBtnRef = React.useRef<HTMLLIElement>(null);
   const issuesBtnRef = React.useRef<HTMLLIElement>(null);
   const [setupChat, setSetupChat] = React.useState({ 
      inputId: '', outputId: '', start: false, isInputHover: false, isOutputHover: false, 
      isNodesUpdated: false, isCanGetOutput: false
   });
   const [tabsChat, setTabsChat] = React.useState<tabChatType[]>([]);
   const [resetValue, setResetValue] = React.useState('');
   const [searchParams] = useSearchParams();
   const isExternalChat = React.useMemo(() => searchParams.get('isExternalChat') === 'true', [searchParams]);
   const { getNodes, getEdges, setNodes, setEdges, fitView } = useReactFlow();
   const nodesLive = useNodes();
   const edgesLive = useEdges();
   const liveIssues = React.useMemo(
      () => collectWorkflowIssues(nodesLive, edgesLive, project),
      [nodesLive, edgesLive, project]
   );
   const issueErrorCount = React.useMemo(
      () => liveIssues.filter((i) => i.severity === 'error').length,
      [liveIssues]
   );
   const issueWarningCount = React.useMemo(
      () => liveIssues.filter((i) => i.severity === 'warning').length,
      [liveIssues]
   );

   // Validation issues badges (shown on nodes above the card).
   // We keep them visible until a Run cycle finishes successfully, then hide them
   // for the same issues snapshot.
   const issuesSignature = React.useMemo(() => {
      return liveIssues
         .map((i) => `${i.nodeId}:${i.field}:${i.severity}`)
         .sort()
         .join('|');
   }, [liveIssues]);

   const issueSeverityByNodeId = React.useMemo(() => {
      const map = new Map<string, 'error' | 'warning'>();
      for (const issue of liveIssues) {
         const current = map.get(issue.nodeId);
         if (!current || (current === 'warning' && issue.severity === 'error')) {
            map.set(issue.nodeId, issue.severity);
         }
      }
      return map;
   }, [liveIssues]);

   const [dismissedIssuesSignature, setDismissedIssuesSignature] = React.useState<string | null>(null);
   const issuesSignatureAtRunStartRef = React.useRef<string | null>(null);

   React.useEffect(() => {
      const shouldHide = dismissedIssuesSignature === issuesSignature;
      setNodes((current) => {
         let changed = false;
         const next = current.map((n) => {
            const desiredSeverity = shouldHide ? undefined : issueSeverityByNodeId.get(n.id);
            const currentSeverity = (n.data as any)?.validationIssueSeverity as string | undefined;
            const normalizedCurrent = currentSeverity || undefined;
            const normalizedDesired = desiredSeverity || undefined;
            if (normalizedCurrent !== normalizedDesired) {
               changed = true;
               return {
                  ...n,
                  data: {
                     ...n.data,
                     validationIssueSeverity: normalizedDesired,
                  },
               };
            }
            return n;
         });
         return changed ? next : current;
      });
   }, [dismissedIssuesSignature, issuesSignature, issueSeverityByNodeId, setNodes]);

   // Real-Time
   React.useEffect(() => {
      const openConnections = () => setState((s) => ({ ...s, isGlobalConnections: true }));
      window.addEventListener(WORKFLOW_CONNECTIONS_OPEN_EVENT, openConnections);
      return () => window.removeEventListener(WORKFLOW_CONNECTIONS_OPEN_EVENT, openConnections);
   }, []);

   React.useEffect(() => {
      const handleWebhook = (data: any) => {
         const projectId = searchParams.get('id');
         // ObjectId serialises to string over socket; compare as strings to be safe
         if (projectId !== String(data.projectId)) return;
         setState(state => ({ ...state, isRunRealTime: !data.isDone }));
         dispatch(reduxUpdateSettings({ isWorkflowRun: !data.isDone }));

         if (data.isFirst) {
            addUpdateNodeToQueue(setNodes, (nodes) =>
               markRunEntryNodesExecuting(
                  clearExecutionProgress(nodes, { clearOutput: true }),
                  getEdges()
               )
            );
         }
         if (data.isDone) {
            addUpdateNodeToQueue(setNodes, (nodes) =>
               nodes.map((node) => ({
                  ...node,
                  data: { ...node.data, isExecuting: false },
               }))
            );
         }
         const nodes = getNodes();
         // Skip meta keys that are not node indices
         const index = Object.keys(data).find(k => !['projectId', 'isFirst', 'isDone', 'nodes'].includes(k));
         if (!index) return;
         const item = nodes.find(node => node.data.index === index);
         const sources: operationType[] = [];

         for (const edge of getEdges()) {
            if (edge.source === item?.id) {
               for (const node of nodes) {
                  if (node.id === edge.target && node.data.action === 'output') {
                     sources.push({ id: node.id, sourceHandle: edge.sourceHandle, ...node.data });
                  }
               }
            }
         }
         // with webhook only
         let webhookExtractorsData: { [key: string]: any } | null = null;

         if (item?.data.action === 'webhook') {
            const mainKey = Object.keys(data[index])[0];
            webhookExtractorsData = { [mainKey]: data[index][mainKey] }

            for (let num in data[index].extractorsData) { // @ts-ignore
               num = parseInt(num);
               webhookExtractorsData[`Extractor-${num+1}`] = data[index].extractorsData[num];
            }
         }

         console.log(data);
         console.log(webhookExtractorsData);

         addUpdateNodeToQueue(setNodes, (nodes) => (nodes).map((node) => {
            const find = sources.find(source => source.id === node.id && node.data.action === 'output');
            const findFromEnd = data.nodes?.find((n: any) => n.id === node.id);
            
            if (find) {
               const final = webhookExtractorsData || data[index]?.Output || data[index];
               return { ...node, data: { ...node.data, 
                  value: formatConnectedOutputValue(final, find.sourceHandle)
               }}
            } else if (findFromEnd && node.data.outputType) {
               return { ...node, data: { ...node.data, value: { ...findFromEnd.data.value, isTemporaryMemory: false }}};
            } else if (node.data.index === index && node.data.action === 'output') {
               const incomingHandle = getEdges().find(edge => edge.target === node.id)?.sourceHandle;
               return {
                  ...node,
                  data: {
                     ...node.data,
                     value: formatConnectedOutputValue(data[index]?.Output || data[index], incomingHandle || 'Output')
                  }
               };
            } else if (node.data.index === index) {
               return { ...node, data: { ...node.data, value: { ...node.data.value, ...(data[index]?.updateNodeValue || {}), isTemporaryMemory: false }}};
            } else if (node.data.outputType) {
               return { ...node, data: { ...node.data, value: { ...node.data.value, isTemporaryMemory: false }}};
            } else return node;
         }));
         if (item?.id) {
            addUpdateNodeToQueue(setNodes, (nodes) =>
               advanceExecutionProgress(nodes, getEdges(), item.id, index, data[index])
            );
         } else {
            addUpdateNodeToQueue(setNodes, (nodes) => (nodes).map((node) => {
               if (node.data.index === index) {
                  const output = data[index];
                  return {
                     ...node,
                     data: {
                        ...node.data,
                        output,
                        isExecuting: false,
                        executionError: isExecutionErrorOutput(output),
                     },
                  };
               } else return node;
            }));
         }
      }
      socket.off('runMonitoringInClient');
      socket.on('runMonitoringInClient', handleWebhook);
      return () => { socket.off('runMonitoringInClient', handleWebhook) }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // loop run
   React.useEffect(() => {
      if (project?.loop.starting && project.loop.value) {
         intervalId = setInterval(handleWorkflowRunGlobal, project.loop.value * 60 * 1000);
      }
      return () => clearInterval(intervalId);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [project?.loop.starting]);

   // ---
   const handleWorkflowRunGlobal = async (targetIndex?: string) => {
      setTriggerNodes(null);
      const workflowId = searchParams.get('id') as string;
      const runIssuesSignatureSnapshot = issuesSignature;
      issuesSignatureAtRunStartRef.current = runIssuesSignatureSnapshot;
      const runRealTime = state.isRunRealTime;
      await handleWorkflowRun(
         workflowId,
         userToken as string,
         getNodes(),
         getEdges(),
         runRealTime,
         state.isRun,
         project?.apiKey as string,
         dispatch,
         navigate,
         setNodes,
         handleValuesFeatures,
         targetIndex
      );

      // Hide the pre-run validation badges after a successful run cycle.
      // "Successful" here means: no runtime node failed (executionError).
      if (!runRealTime) {
         const nodesAfter = getNodes();
         const hasRuntimeErrors = nodesAfter.some((n) => Boolean(n.data?.executionError));
         if (!hasRuntimeErrors) {
            setDismissedIssuesSignature(runIssuesSignatureSnapshot);
         } else {
            setDismissedIssuesSignature(null);
         }
      }
      handleWorkflowSave(workflowId, userToken as string, getNodes(), getEdges())
   }

   const proceedAfterValidation = (nodes = getNodes()) => {
      // A node with no incoming edge is a graph root — a valid place to start a run from —
      // regardless of whether its action is a "real" trigger. This covers e.g. a bare
      // Input block used to feed test data into a branch without a live trigger event.
      const targetIds = new Set(getEdges().map(e => e.target));
      const triggers = nodes.filter(n =>
         TRIGGER_ACTIONS.has(n.data?.action) ||
         (n.data?.action !== 'output' && !targetIds.has(n.id))
      );
      if (triggers.length > 1) {
         setTriggerNodes(triggers.map(n => ({ index: n.data.index as string, name: (n.data.name || n.data.index) as string })));
      } else {
         handleWorkflowRunGlobal();
      }
   };

   const handleRunClick = () => {
      if (state.isRun) { handleWorkflowRunGlobal(); return; }
      const nodes = getNodes();
      // Issues stay on the Issues button — Run should start without opening that panel.
      proceedAfterValidation(nodes);
   };
   // ---
   const handleAutoLayout = async () => {
      const laid = await applyAutoLayout(getNodes(), getEdges(), 'LR');
      setNodes(laid);
      // Fit after positions commit — slightly more padding so stacked flows breathe.
      setTimeout(() => fitView({ duration: 450, padding: 0.14, maxZoom: 1 }), 60);
   };

   // ---
   const importInputRef = React.useRef<HTMLInputElement>(null);
   const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ''; // reset so importing the same file twice in a row still fires onChange
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
         try {
            const parsed = JSON.parse(reader.result as string);
            if (!Array.isArray(parsed?.nodes) || !Array.isArray(parsed?.edges)) {
               alert('This file does not look like a valid workflow export (missing nodes/edges).');
               return;
            }
            if (!window.confirm('This will replace everything on the current canvas with the imported workflow. Continue?')) return;

            setNodes(parsed.nodes);
            setEdges(parsed.edges);
            setTimeout(() => fitView({ duration: 400, padding: 0.1 }), 50);
         } catch (err) {
            console.log(err);
            alert('Could not read this file as valid JSON.');
         }
      };
      reader.readAsText(file);
   };

   // ---
   const handleValuesFeatures = (values: any) => setState(state => ({ ...state, ...values }));

   const handleRunAll = () => {
      setTriggerNodes(null);
      handleWorkflowRunGlobal();
   };

   return (
      <Main>
         <div className="flex">
            <div
               className="back"
               title="Back to workflows"
               aria-label="Back to workflows"
               onClick={ _=> {
                  if (window.top) window.top.location.href = `${consoleURL}/workflow-builder`
               }}
               >
               <i className="ti ti-arrow-left" aria-hidden="true" />
               <span>Back</span>
            </div>

            <ul className="group">
               <li
                  id="running"
                  ref={ runBtnRef }
                  className={ `run ${ state.isRun ? 'running' : state.isRunRealTime ? 'running disabled' : '' }` }
                  title="Run this workflow"
                  aria-label="Run"
                  onClick={ handleRunClick }
               >
                  { state.isRun || state.isRunRealTime ? <AiOutlineLoading3Quarters /> : <i className="ti ti-player-play" aria-hidden="true" /> }
                  { state.isRunRealTime ? <span>Live</span>  : (state.isRun ? <span>Stop</span> : <span>Run</span>) }
               </li>
               <li
                  className={ project?.loop.starting ? 'loop' : '' }
                  title="Configure scheduled runs"
                  aria-label="Loop"
                  onClick={ _=> setState(state => ({ ...state, isLoop: true })) }
               >
                  <i className="ti ti-refresh" aria-hidden="true" />
                  <span>Loop</span>
               </li>
               <li
                  title="Manage connections used by this workflow"
                  aria-label="Connections"
                  onClick={ _=> setState(state => ({ ...state, isGlobalConnections: true })) }
               >
                  <i className="ti ti-affiliate" aria-hidden="true" />
                  <span>Connections</span>
               </li>
               <li
                  ref={ issuesBtnRef }
                  className={ `issues ${issueErrorCount ? 'has-errors' : issueWarningCount ? 'has-warnings' : ''}` }
                  title={
                     liveIssues.length
                        ? `${issueErrorCount} critical · ${issueWarningCount} performance`
                        : 'Review critical blockers and performance tips before Run'
                  }
                  aria-label="Issues"
                  onClick={ () => toggleWorkflowIssuesPanel() }
               >
                  <AlertTriangle size={16} />
                  <span>Issues</span>
                  {liveIssues.length > 0 ? (
                     <em className="issues-badge">{liveIssues.length}</em>
                  ) : null}
               </li>
            </ul>

            <ul className="group icon-only">
               <li
                  className={ !canUndo ? 'muted' : '' }
                  title="Undo (Ctrl+Z)"
                  aria-label="Undo"
                  onClick={ onUndo }
               >
                  <i className="ti ti-arrow-back-up" aria-hidden="true" />
               </li>
               <li
                  className={ !canRedo ? 'muted' : '' }
                  title="Redo (Ctrl+Y)"
                  aria-label="Redo"
                  onClick={ onRedo }
               >
                  <i className="ti ti-arrow-forward-up" aria-hidden="true" />
               </li>
            </ul>

            <ul className="group">
               <li
                  title="Save this workflow"
                  aria-label="Save"
                  onClick={ _=> { if (!isWorkflowRun) handleWorkflowSave(searchParams.get('id') as string, userToken as string, getNodes(), getEdges())} }
               >
                  <i className="ti ti-device-floppy" aria-hidden="true" />
                  <span>Save</span>
               </li>
               <li
                  className="icon-only"
                  title="Export this workflow as a template JSON file"
                  aria-label="Export"
                  onClick={ _=> setState(state => ({ ...state, isExport: true })) }
               >
                  <i className="ti ti-external-link" aria-hidden="true" />
               </li>
               <li
                  className="icon-only"
                  title="Import a workflow JSON file, replacing the current canvas"
                  aria-label="Import"
                  onClick={ _=> importInputRef.current?.click() }
               >
                  <i className="ti ti-upload" aria-hidden="true" />
               </li>
               <input
                  ref={ importInputRef }
                  type="file"
                  accept=".json,application/json"
                  style={{ display: 'none' }}
                  onChange={ handleImportFile }
               />
            </ul>

            <div
               className="layout-toggle"
               title="Auto-arrange: stack flows top→bottom, keep related nodes together"
               aria-label="Auto-arrange nodes"
               onClick={ handleAutoLayout }
            >
               <i className="ti ti-layout-grid" aria-hidden="true" />
            </div>
         </div>
         { triggerNodes && (
            <TriggerSelect
               triggers={ triggerNodes }
               anchorRef={ runBtnRef }
               onSelect={ (index) => handleWorkflowRunGlobal(index) }
               onRunAll={ handleRunAll }
               onClose={ () => setTriggerNodes(null) }
            />
         )}
         {
            state.isLoop && project?.loop ? <LoopProject project={ project } setProject={ setProject } handleClose={ () => handleValuesFeatures({ isLoop: false }) } /> :
            state.isGlobalConnections && project ? <GlobalConnectionsPanel project={ project } setProject={ setProject } handleClose={ () => handleValuesFeatures({ isGlobalConnections: false }) } nodeActions={ getNodes().map(n => n.data?.action as string) } /> :
            state.isExport ? <ExportProject projectId={ project?._id } handleValuesFeatures={ handleValuesFeatures } /> :
            state.isChat || isExternalChat ? <ChatProject projectId={ project?._id } setup={ setupChat } setSetup={ setSetupChat } tabsChat={ tabsChat } setTabsChat={ setTabsChat } handleWorkflowRun={ handleWorkflowRunGlobal } handleValuesFeatures={ handleValuesFeatures } /> :
            state.isAPI ? <APIProject data={ project } handleValuesFeatures={ handleValuesFeatures } /> :
            state.isWebChat ? <WebChatProject project={ project } handleValuesFeatures={ handleValuesFeatures } /> : null
         }
         { state.isReset ? createPortal(
            <PopupOverlay className="reset" onClick={ _=> setState(state => ({ ...state, isReset: false }))}>
               <div onClick={ e => e.stopPropagation() }>
                  <div className="close" onClick={ _=> setState(state => ({ ...state, isReset: false }))}>
                     <X size={ 19 } />
                  </div>
                  <div className="content">
                     <div className="title">
                        <div className="icon"><AlertTriangle size={ 20 } /></div>
                        <h3>Reset ({ project?.name })</h3>
                     </div>
                     <div className="info">
                        <div>
                           <span>Are you sure you want to reset the project?</span>
                           <span>This action will permanently delete all workflow data, connections, and configurations. This cannot be undone.</span>
                        </div>
                        <label>Type “{ project?.name }” below to confirm:</label>
                        <TextInput placeholder={`Enter '${project?.name}' to confirm`} value={ resetValue } isNormal handleChange={ e => setResetValue(e.target.value) } />
                     </div>
                     <div className="tip">
                        <span><AlertTriangle size={ 18 } /></span>
                        <span><span>Warning:</span> This will remove all nodes, connections, and saved workflow configurations. Make sure you have exported any important workflows before proceeding.</span>
                     </div>
                     <div className="action">
                        <button onClick={ _=> setState(state => ({ ...state, isReset: false }))} className="cancel">Cancel</button>
                        <button 
                           onClick={ _=> { 
                              setNodes([]); setEdges([]); 
                              setState(state => ({ ...state, isReset: false }));
                              setResetValue('');
                           }}
                           disabled={ resetValue !== project?.name }
                        >Reset Project</button>
                     </div>
                  </div>
               </div>
            </PopupOverlay>,
            document.body
         ) : null }
      </Main>
   );
}

// ── Trigger selector dropdown ──────────────────────────────────────────────
function TriggerSelect({
   triggers, anchorRef, onSelect, onRunAll, onClose
}: {
   triggers: Array<{ index: string, name: string }>;
   anchorRef: React.RefObject<HTMLLIElement>;
   onSelect: (index: string) => void;
   onRunAll: () => void;
   onClose: () => void;
}) {
   const dropRef = React.useRef<HTMLDivElement>(null);
   const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

   const updatePosition = React.useCallback(() => {
      if (!anchorRef.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      const width = 320;
      // Prefer left-aligned under Run; clamp to viewport
      let left = r.left;
      left = Math.max(12, Math.min(left, window.innerWidth - width - 12));
      const top = Math.min(r.bottom + 10, window.innerHeight - 24);
      setPos({ top, left });
   }, [anchorRef]);

   React.useLayoutEffect(() => {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
         window.removeEventListener('resize', updatePosition);
         window.removeEventListener('scroll', updatePosition, true);
      };
   }, [updatePosition]);

   React.useEffect(() => {
      const handle = (e: MouseEvent) => {
         if (dropRef.current && !dropRef.current.contains(e.target as Node) &&
            anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
            onClose();
         }
      };
      document.addEventListener('mousedown', handle, true);
      return () => document.removeEventListener('mousedown', handle, true);
   }, [anchorRef, onClose]);

   if (!pos || typeof document === 'undefined') return null;

   return createPortal(
      <TriggerDropdown
         ref={ dropRef }
         style={{ top: pos.top, left: pos.left }}
         role="menu"
         aria-label="Choose trigger to run"
      >
         <div className="head">
            <div className="head-top">
               <span className="eyebrow">Choose trigger</span>
               <span className="count">{triggers.length}</span>
            </div>
            <p>Pick an entry point, or run everything at once.</p>
         </div>
         <button
            type="button"
            className="run-all"
            onClick={ onRunAll }
         >
            <span className="ic"><ListChecks size={17} /></span>
            <span className="copy">
               <strong>Run All</strong>
               <span>Execute every entry point together</span>
            </span>
            <span className="go" aria-hidden="true">↵</span>
         </button>
         <div className="divider">
            <span>Or run one</span>
         </div>
         <div className="list">
            { triggers.map((t) => (
               <button
                  type="button"
                  key={ t.index }
                  className="item"
                  onClick={ () => onSelect(t.index) }
               >
                  <span className="ic"><Zap size={15} /></span>
                  <span className="copy">
                     <strong>{ t.name }</strong>
                     <span>{ t.index }</span>
                  </span>
                  <span className="play" aria-hidden="true">
                     <i className="ti ti-player-play" />
                  </span>
               </button>
            )) }
         </div>
      </TriggerDropdown>,
      document.body
   );
}